import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from 'src/constants/routes';
import { useLocationSearch } from 'src/helpers/hooks/useLocationSearch';
import { Loader } from 'src/pages/StartApplication/StartApplication.loader';
import {
  getVerifyPolicyComplianceProvider,
  getVerifyPolicyInput,
} from 'src/redux/selectors/verifyPolicy';
import { displayErrorToast } from '../utils/display-error-toast.util';
import { validateVerifyPolicyInputData } from '../utils/validate-verify-policy-input-data.util';

interface VerifyPolicyDataGuardProps {
  children: ReactNode;
}

type VerifyPolicyDataGuardLoadingState = 'loading' | 'parsed';

export function VerifyPolicyDataGuard({
  children,
}: VerifyPolicyDataGuardProps) {
  const [loadingStatus, setLoadingStatus] =
    useState<VerifyPolicyDataGuardLoadingState>('loading');

  const navigate = useNavigate();
  const getUrlWithQueryParams = useLocationSearch();

  const verifyPolicyInput = useSelector(getVerifyPolicyInput);
  const verifyPolicyComplianceProviderData = useSelector(
    getVerifyPolicyComplianceProvider,
  );
  const isValidQuoteData = useMemo(
    () => validateVerifyPolicyInputData(verifyPolicyInput),
    [verifyPolicyInput],
  );
  const isComplianceDirectLink = useMemo(
    () => verifyPolicyComplianceProviderData.isCovieDirectLink,
    [verifyPolicyComplianceProviderData],
  );
  const shouldRedirectToComplianceForm = useMemo(
    () => !isValidQuoteData && !isComplianceDirectLink,
    [isValidQuoteData, isComplianceDirectLink],
  );

  useEffect(() => {
    if (!shouldRedirectToComplianceForm) {
      setLoadingStatus('parsed');
      return;
    }

    navigate(getUrlWithQueryParams(ROUTES.COMPLIANCE_PATH), {
      replace: true,
    });
    displayErrorToast('Direct Link contains invalid params');
  }, [getUrlWithQueryParams, navigate, shouldRedirectToComplianceForm]);

  if (loadingStatus === 'loading') {
    return <Loader />;
  }

  return <>{children}</>;
}
